import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSelectedSubscription } from '@/lib/use-selected-subscription';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { IconChip } from '@/components/ui/calm';
import { SkeletonRows } from '@/components/ui/skeleton';
import { Smartphone, Laptop, Trash2, RotateCcw, ShieldOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getWebApp, haptic } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { shortDateYear } from '@/lib/date';
import { toast } from 'sonner';
import type { BlockedDevice, Device } from '@/api/types';

export const Route = createFileRoute('/devices')({
  component: Devices,
});

// Онлайн = last_seen свежее 15 минут (AG обновляет метку при запросах конфига).
const ONLINE_THRESHOLD_MS = 15 * 60_000;

function isOnline(lastSeen: string): boolean {
  const ts = Date.parse(lastSeen);
  return Number.isFinite(ts) && Date.now() - ts < ONLINE_THRESHOLD_MS;
}

function formatLastSeen(lastSeen: string): string | null {
  const ts = Date.parse(lastSeen);
  if (!Number.isFinite(ts)) return null;
  const diffMin = Math.floor((Date.now() - ts) / 60_000);
  if (diffMin < 5) return i18n.t('Just now');
  if (diffMin < 60) return i18n.t('Min ago', { count: diffMin });
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return i18n.t('Hours ago', { count: diffH });
  if (diffH < 48) return i18n.t('Yesterday');
  return shortDateYear(new Date(ts));
}

function Devices() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { sub, isPending: subPending } = useSelectedSubscription();
  const { data: devices, isPending: devicesPending } = useQuery({
    queryKey: ['devices', sub?.id],
    queryFn: () => (sub ? api.devices(sub.id) : Promise.resolve([] as Device[])),
    enabled: !!sub,
  });
  // Отключённые: те, кому мы перестали отдавать конфиг. Раньше их не показывали
  // вовсе, и человек, отключивший собственный телефон, видел в VPN-клиенте
  // «Устройство отключено» без единого способа это отменить.
  const { data: blocked } = useQuery({
    queryKey: ['device-blocks', sub?.id],
    queryFn: () => (sub ? api.deviceBlocks(sub.id) : Promise.resolve([] as BlockedDevice[])),
    enabled: !!sub,
  });
  const [pendingDelete, setPendingDelete] = useState<Device | null>(null);

  // Пока список едет, `devices` пуст — без этой ветки экран уверенно сообщал
  // «Нет устройств» человеку, у которого их два. Запрос ждёт подписку
  // (`enabled: !!sub`), поэтому его pending учитываем только когда она есть.
  const loading = subPending || (!!sub && devicesPending);

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/subscription' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  const { mutate: deleteDevice } = useMutation({
    mutationFn: (id: string) => {
      if (!sub) throw new Error('no sub');
      return api.deleteDevice(sub.id, id).then(() => id);
    },
    onSuccess: (id) => {
      qc.setQueryData<Device[]>(['devices', sub?.id], (prev) => (prev || []).filter((d) => d.id !== id));
      void qc.invalidateQueries({ queryKey: ['device-blocks', sub?.id] });
      haptic('success');
      toast.success(t('Device disconnected'));
    },
    onError: () => {
      haptic('error');
      toast.error(t('Delete failed'));
    },
  });

  const { mutate: unblockDevice, isPending: unblocking } = useMutation({
    mutationFn: (hwid: string) => {
      if (!sub) throw new Error('no sub');
      return api.unblockDevice(sub.id, hwid);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['device-blocks', sub?.id] });
      void qc.invalidateQueries({ queryKey: ['devices', sub?.id] });
      haptic('success');
      toast.success(t('Device reconnecting'));
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || t('Device restore failed'));
    },
  });

  return (
    <>
      <div className="p-4 space-y-4 pb-24">
        <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] mb-6 px-2 text-foreground">{t('Devices')}</h2>

        <div className="space-y-3">
          {loading ? (
            <SkeletonRows count={2} itemClassName="rounded-[var(--r-card)] p-4" />
          ) : !devices || devices.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center calm-card rounded-[var(--r-card)]">
              <IconChip icon={Smartphone} tone="blue" size={64} radius={18} className="mb-4" />
              <h3 className="font-semibold mb-1 text-foreground">{t('No devices')}</h3>
              <p className="text-sm text-[var(--t-muted)]">{t('No devices desc')}</p>
            </div>
          ) : (
            devices.map((dev) => {
              const online = isOnline(dev.lastSeen);
              const lastSeenLabel = formatLastSeen(dev.lastSeen);
              // Поставщик не прислал, когда устройство было в сети: показываем хотя бы,
              // когда оно подключилось впервые.
              const firstSeenLabel = dev.lastSeen ? null : formatLastSeen(dev.firstSeen);
              return (
              <Card key={dev.id} className="relative overflow-hidden">
                {dev.isNew && (
                  <div className="absolute top-0 right-0 bg-[var(--t-ok)] text-black text-[10px] font-bold px-2 py-0.5 rounded-bl-xl">
                    NEW
                  </div>
                )}
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex gap-3 items-center min-w-0">
                    <IconChip icon={dev.os === 'iOS' || dev.os === 'Android' ? Smartphone : Laptop} tone="blue" size={40} />
                    <div className="min-w-0">
                      <div className="font-semibold truncate text-foreground">{dev.name}</div>
                      <div className="text-xs text-[var(--t-muted)] truncate flex items-center gap-1.5">
                        <span
                          className={`w-1.5 h-1.5 rounded-full shrink-0 ${online ? 'bg-[var(--t-ok-strong)]' : 'bg-[var(--t-faint)]'}`}
                          
                        />
                        {dev.os} • {dev.ip}
                      </div>
                      <div className="text-[11px] text-[var(--t-faint)] truncate">
                        {online
                          ? t('Online')
                          : lastSeenLabel
                            ? t('Was online', { when: lastSeenLabel })
                            : firstSeenLabel
                              ? t('Connected since', { when: firstSeenLabel })
                              : t('Activity unknown')}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingDelete(dev)}
                    className="relative grid size-10 shrink-0 place-items-center rounded-[var(--r-sm)] bg-[var(--t-surface-2)] text-[var(--t-muted)] transition-colors active:text-[var(--t-danger)] after:absolute after:-inset-1 after:content-['']"
                    aria-label={`${t('Disconnect')} ${dev.name}`}
                  >
                    <Trash2 className="size-[18px]" />
                  </button>
                </CardContent>
              </Card>
              );
            })
          )}
        </div>

        {blocked && blocked.length > 0 && (
          <div className="space-y-3 pt-2">
            <div className="px-2">
              <h3 className="text-sm font-bold text-foreground">{t('Disconnected devices')}</h3>
              <p className="text-[11.5px] text-[var(--t-muted)] mt-0.5 leading-snug">
                {t('Disconnected desc')}
              </p>
            </div>
            {blocked.map((b) => (
              <Card key={b.hwid}>
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="flex gap-3 items-center min-w-0">
                    <IconChip icon={ShieldOff} tone="neutral" size={40} />
                    <div className="min-w-0">
                      <div className="font-semibold truncate text-foreground">{b.name}</div>
                      <div className="text-[11px] text-[var(--t-faint)] truncate">
                        {t('Disconnected at', { when: formatLastSeen(b.blocked_at) ?? '' })}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      haptic('light');
                      unblockDevice(b.hwid);
                    }}
                    disabled={unblocking}
                    className="relative shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-[11px] text-xs font-bold text-foreground disabled:opacity-50 active:scale-[0.97] transition-transform after:absolute after:inset-x-0 after:-inset-y-1.5 after:content-['']"
                    style={{ background: 'var(--t-surface-2)' }}
                  >
                    <RotateCcw className="w-4 h-4" /> {t('Restore')}
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && deleteDevice(pendingDelete.id)}
        title={t('Disconnect device?')}
        description={
          pendingDelete
            ? t('Disconnect device desc', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('Disconnect')}
        destructive
      />
    </>
  );
}
